import { FEATURE_KEYS, LIMIT_KEYS, type FeatureKeyT, type LimitKeyT, type Problem } from "@malfak/contracts";
import { toast } from "sonner";
import { isApiError } from "@/lib/api/errors";
import { fmtDate, fmtNumber } from "@/lib/format";
import { i18n } from "@/lib/i18n";

/**
 * Friendly messages for plan problems (ALP-80/81): `limit_reached` (429) and `feature_not_in_plan` (403) carry an
 * EntitlementDenial in `details` (limit/feature, max, used, period, resetAt). The AI budget gets its own wording:
 * "AI budget for this month is used up" (+ reset date).
 */
export type PlanProblem = {
  kind: "ai_budget" | "limit" | "feature";
  message: string;
  /** Secondary line: reset date, or null. */
  detail: string | null;
  limit?: LimitKeyT;
  feature?: FeatureKeyT;
  resetAt?: string | null;
};

const asProblem = (x: unknown): Problem | null => {
  if (isApiError(x)) return x.problem;
  if (x && typeof x === "object" && "title" in x && "status" in x) return x as Problem;
  return null;
};

const fmtAmount = (key: LimitKeyT, n: unknown) => {
  if (typeof n !== "number") return "–";
  return key === "ai_budget_usd" ? `$${n.toFixed(2)}` : fmtNumber(n, i18n.language === "ar" ? "ar" : "en");
};

/** Map an ApiError / problem to a plan message, or null when it isn't a plan problem. */
export function planProblem(x: unknown): PlanProblem | null {
  const p = asProblem(x);
  if (!p) return null;
  const d = (p.details ?? {}) as { limit?: string; feature?: string; max?: number | null; used?: number; resetAt?: string | null };
  const t = i18n.t.bind(i18n) as unknown as (k: string, o?: Record<string, unknown>) => string;
  const reset = d.resetAt ? fmtDate(d.resetAt, { day: "numeric", month: "long" }) : null;
  if (p.title === "limit_reached") {
    const limit = (LIMIT_KEYS as readonly string[]).includes(d.limit ?? "") ? (d.limit as LimitKeyT) : undefined;
    if (limit === "ai_budget_usd" || limit === "ai_tokens") {
      return { kind: "ai_budget", limit, message: t("plans.errors.aiBudget"), detail: reset ? t("plans.errors.aiBudgetReset", { date: reset }) : null, resetAt: d.resetAt ?? null };
    }
    const what = limit ? t(`plans.limits.${limit}`) : t("plans.gate.limitReached");
    return {
      kind: "limit", limit,
      message: limit ? t("plans.errors.limit", { what, used: fmtAmount(limit, d.used), max: fmtAmount(limit, d.max) }) : what,
      detail: reset ? t("plans.errors.limitReset", { date: reset }) : null,
      resetAt: d.resetAt ?? null,
    };
  }
  if (p.title === "feature_not_in_plan") {
    const feature = (FEATURE_KEYS as readonly string[]).includes(d.feature ?? "") ? (d.feature as FeatureKeyT) : undefined;
    return { kind: "feature", feature, message: feature ? t("plans.errors.feature", { what: t(`plans.features.${feature}`) }) : t("plans.gate.notInPlan"), detail: null };
  }
  return null;
}

const ADMIN_TOAST_KEY = "malfak.plans.aiBudgetToast";

/**
 * Show a plan problem as a toast. For the AI budget, owners/admins also get a one-per-session explanation of how to
 * raise it. Returns true when the error was a plan problem (callers skip their generic error toast).
 */
export function toastPlanProblem(x: unknown, opts: { admin?: boolean } = {}): boolean {
  const pp = planProblem(x);
  if (!pp) return false;
  toast.error(pp.message, { id: `plan-${pp.kind}-${pp.limit ?? pp.feature ?? ""}`, description: pp.detail ?? undefined });
  if (pp.kind === "ai_budget" && opts.admin) notifyAdminBudget();
  return true;
}

/** Once per browser session: tell an owner/admin the org's AI budget is used up. */
export function notifyAdminBudget() {
  try {
    if (sessionStorage.getItem(ADMIN_TOAST_KEY)) return;
    sessionStorage.setItem(ADMIN_TOAST_KEY, "1");
  } catch {
    /* storage blocked: still show it */
  }
  toast.warning(i18n.t("plans.errors.aiBudgetAdmin"), { id: "plan-ai-budget-admin", duration: 12_000 });
}
